import type { FC } from "react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import { CHAT_MODE, useChatState } from "@/store/chat-state";
import { RAG_LOADING_MESSAGES } from "@/constants/rag-loading-messages";
import { ERROR_MESSAGES } from "@/constants/error-messages";
import { LoadingIterator } from "@/components/primitives/loading-iterator";
import { AnimatedSection } from "@/components/primitives/animated-section";
import { SourcesGrid } from "@/components/drawer-components/sources-grid";
import { CollapsedResponseContainer } from "@/components/primitives/collapsed-response-container";
import {
  BodyList,
  BodyListItem,
  BodyRegular,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  NewTabBodyLink,
  OrderedBodyList,
} from "@/components/primitives/typography";
import {
  AnswerContainer,
  ChatContentArea,
  MarkdownContent,
} from "./chat-components";
import { Landing } from "./landing";
import { UserFeedbackComponent } from "../user-feedback";
import { SuggestedQuestions } from "./suggested-questions";
import { HelpfulNextSteps } from "../helpful-next-steps";
import { CheckIcon, CopyIcon } from "lucide-react";
import { useClipboard } from "@/hooks/use-clipboard";
import styled from "@emotion/styled";
import { spacing } from "@/components/design-tokens";
import type { ChatParts } from "@/types/chat-parts";
import { safeUrlTransform } from "@/utils/url-helpers";

const StyledP = styled(BodyRegular)`
  margin-bottom: ${spacing.md}px;
`;

// Custom components for ReactMarkdown to use our typography
const markdownComponents: Components = {
  p: StyledP,
  a: NewTabBodyLink,
  ul: BodyList,
  ol: OrderedBodyList,
  li: BodyListItem,
  h1: Heading1,
  h2: Heading2,
  h3: Heading3,
};

type ChatContentProps = {
  userQuery: string;
  status: "error" | "submitted" | "streaming" | "ready";
  response: ChatParts | undefined;
  mode: (typeof CHAT_MODE)[keyof typeof CHAT_MODE];
  error?: Error;
};

export const ChatContent: FC<ChatContentProps> = ({
  userQuery,
  status,
  response,
  mode,
  error,
}) => {
  const { setUserQuery, setMode, setContextUrl } = useChatState();

  const {
    content,
    sources,
    followUpQuestions,
    helpfulNextSteps,
    traceId,
    observationId,
  } = response ?? {};
  const { contentRef, hasCopied, copyToClipboard } = useClipboard();

  const isLoading = status === "streaming" || status === "submitted";
  const showLanding = mode === CHAT_MODE.INPUT && !userQuery;
  const showResponse
    = mode === CHAT_MODE.ANSWERING || (mode === CHAT_MODE.INPUT && userQuery);

  const handleQuestionClick = (question: string) => {
    setContextUrl(undefined);
    setMode(CHAT_MODE.INPUT);
    setUserQuery(question);
  };

  return (
    <ChatContentArea>
      {showLanding && <Landing />}

      {showResponse && (
        <AnswerContainer>
          {/* Show user query as title */}
          {userQuery && <Heading4>{userQuery}</Heading4>}

          {/* Show loading or content */}
          <MarkdownContent ref={contentRef}>
            {error && status === "error" && (
              <ReactMarkdown components={markdownComponents}>
                {ERROR_MESSAGES.GENERIC_CHAT_ERROR}
              </ReactMarkdown>
            )}

            {(isLoading || status === "ready") && content && !error && (
              <ReactMarkdown
                components={markdownComponents}
                urlTransform={safeUrlTransform}
              >
                {content}
              </ReactMarkdown>
            )}

            {isLoading && !content && !error && (
              <LoadingIterator
                messages={RAG_LOADING_MESSAGES}
                interval={2000}
              />
            )}
          </MarkdownContent>

          <AnimatedSection
            gap="4xl"
            animationType="fadeSlide"
            isVisible={status === "ready"}
          >
            <UserFeedbackComponent
              traceId={traceId}
              observationId={observationId}
              additionalAction={{
                label: hasCopied ? "Copied!" : "Copy answer",
                icon: hasCopied ? (
                  <CheckIcon size={16} strokeWidth={2} />
                ) : (
                  <CopyIcon size={16} strokeWidth={2} />
                ),
                onClick: copyToClipboard,
              }}
            />

            {/* Sources */}
            <AnimatedSection
              heading={`Sources (${sources?.length ?? 0})`}
              gap="2xl"
              animationType="none"
              isVisible={(sources?.length ?? 0) > 0}
            >
              <CollapsedResponseContainer
                shouldCollapseInitially={(sources?.length ?? 0) > 2}
                collapsedHeight={120}
              >
                <SourcesGrid sources={sources ?? []} />
              </CollapsedResponseContainer>
            </AnimatedSection>

            {/* Show follow up questions */}

            <AnimatedSection
              heading="Follow up questions"
              gap="2xl"
              animationType="none"
              isVisible={(followUpQuestions?.length ?? 0) > 0}
            >
              <SuggestedQuestions
                questions={followUpQuestions ?? []}
                onQuestionClick={handleQuestionClick}
              />
            </AnimatedSection>

            {/* Show helpful next steps */}
            <HelpfulNextSteps actions={helpfulNextSteps} />
          </AnimatedSection>
        </AnswerContainer>
      )}
    </ChatContentArea>
  );
};
